import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, Signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgScrollbarModule } from 'ngx-scrollbar';

import { NotificationVisibilityTrackerDirective } from '../../../directives/notification-visibility-tracker/notification-visibility-tracker.directive';
import { ActivityLogItem } from '../../../entities/activity-tracking/activity-log-item.entities';
import { AuthService } from '../../../services/auth/auth.service';
import { ActivityLogStore } from '../../../store/activity-log.store';
import { AuthStore } from '../../../store/auth.store';
import { FarmStore } from '../../../store/farm.store';
import { LayoutStore } from '../../../store/layout.store';
import { RouterFacade } from '../../../store/router.facade';
import { TaskStore } from '../../../store/task.store';
import { UserStore } from '../../../store/user.store';
import { IconComponent } from '../../base-components/icon/icon.component';
import { Mat3ButtonComponent } from '../../base-components/mat3-button/mat3-button.component';
import { TaskPanelMenuComponent } from '../../widgets/task-panel-menu/task-panel-menu.component';
import { LangSelectorComponent } from '../lang-selector/lang-selector.component';
import { BrandingComponent } from '../sidebar/branding.component';
import { notifications } from './header.model';
import { SearchDialogComponent } from './search-dialog.component';
import { WeatherData, WeatherMenuComponent } from './weather-menu/weather-menu.component';

interface Language {
  language: string;
  code: string;
  type?: string;
  icon: string;
}

@Component({
  selector: 'app-header',
  imports: [
    RouterModule,
    NgScrollbarModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    LangSelectorComponent,
    Mat3ButtonComponent,
    IconComponent,
    BrandingComponent,
    TranslatePipe,
    WeatherMenuComponent,
    TaskPanelMenuComponent,
    NotificationVisibilityTrackerDirective,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private layoutStore = inject(LayoutStore);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private authStore = inject(AuthStore);
  private userStore = inject(UserStore);
  private farmStore = inject(FarmStore);
  private routerFacade = inject(RouterFacade);
  private activityLogStore = inject(ActivityLogStore);
  private taskStore = inject(TaskStore);

  public isDarkTheme: Signal<boolean> = this.layoutStore.isDarkTheme;
  public showFiller = false;
  public activeMenus: { [key: string]: boolean } = {};
  public userEmail = computed(() => this.userStore.userEmail());
  public userName = computed(() => this.userStore.userName());
  public isAuthenticated = computed(() => this.authStore.isAuthenticated());

  public farms = computed(() => this.farmStore.farms());
  public currentFarm = computed(() => this.farmStore.currentFarm());
  public hasFarms = computed(() => this.farmStore.hasFarms());
  public currentFarmName = computed(() => this.farmStore.currentFarmName());
  public currentFarmNameMobile = computed(() => {
    const farmName = this.farmStore.currentFarmName();
    if (!farmName) return '';
    return farmName.length > 14 ? `${farmName.substring(0, 14)}...` : farmName;
  });

  public userGivenName = computed(() => this.userStore.userGivenName());
  public userFamilyName = computed(() => this.userStore.userFamilyName());
  public userFullName = computed(() => this.userStore.userFullName());
  public userPreferredUsername = computed(() => this.userStore.userPreferredUsername());
  public userPicture = computed(() => this.userStore.userPicture());
  public userEmailVerified = computed(() => this.userStore.userEmailVerified());

  public userProfile = computed(() => {
    const email = this.userEmail();
    if (!email) return null;

    return {
      email,
      given_name: this.userGivenName(),
      family_name: this.userFamilyName(),
      full_name: this.userFullName(),
      picture: this.userPicture(),
      preferred_username: this.userPreferredUsername(),
      email_verified: this.userEmailVerified(),
    };
  });

  public userInitials = computed(() => this.userStore.userInitials());

  public displayName = computed(() => {
    const profile = this.userProfile();
    if (!profile) return '';

    if (profile.full_name) return profile.full_name;
    if (profile.given_name || profile.family_name) {
      return `${profile.given_name} ${profile.family_name}`.trim();
    }
    if (profile.preferred_username) return profile.preferred_username;
    return profile.email?.split('@')[0] || '';
  });

  public showToggle = input(true);
  public toggleChecked = input(false);

  public toggleMobileNav = output<void>();
  public toggleCollapsed = output<void>();


  public ngOnInit() {
    this.translate.setDefaultLang('en');

    const currentFarm = this.farmStore.currentFarm();
    const canShowWeather = this.farmStore.canShowWeather();

    if (currentFarm && !this.activityLogStore.activityLogs().length) {
      this.activityLogStore.loadActivityLogs({ skip: 0, limit: 15 });
    }

    if (canShowWeather && !this.farmStore.weatherForecast() && !this.farmStore.isWeatherLoading()) {
      this.farmStore.loadWeatherForecast();
    }

    if (currentFarm && !this.taskStore.tasks().length && !this.taskStore.isLoading()) {
      this.taskStore.loadTasks({ skip: 0, limit: 20 });
    }
  }

  public openSearchDialog() {
    const dialogRef = this.dialog.open(SearchDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'search-dialog-overlay',
      hasBackdrop: false,
      position: { top: '0', left: '0' }
    });
    this.onMenuOpened('search');

    dialogRef.afterClosed().subscribe(() => {
      this.onMenuClosed('search');
    });
  }

  public changeLanguage(lang: Language): void {
    this.translate.use(lang.code);
    this.selectedLanguage = lang;
  }

  public toggleTheme() {
    this.layoutStore.toggleTheme();
  }

  public selectedLanguage: Language = {
    language: 'English',
    code: 'en',
    type: 'US',
    icon: '/assets/images/flag/icon-flag-en.svg',
  };

  public languages: Language[] = [
    {
      language: 'English',
      code: 'en',
      type: 'US',
      icon: '/assets/images/flag/icon-flag-en.svg',
    },
    {
      language: 'Español',
      code: 'es',
      icon: '/assets/images/flag/icon-flag-es.svg',
    },
    {
      language: 'Français',
      code: 'fr',
      icon: '/assets/images/flag/icon-flag-fr.svg',
    },
    {
      language: 'German',
      code: 'de',
      icon: '/assets/images/flag/icon-flag-de.svg',
    },
  ];

  public activityLogs = computed(() => this.activityLogStore.activityLogs());
  public activityLogsCount = computed(() => this.activityLogStore.totalActivityLogs());
  public isLoadingActivityLogs = computed(() => this.activityLogStore.isLoading());
  public hasMoreActivityLogs = computed(() => this.activityLogStore.hasMoreActivityLogsAvailable());

  public lastSeenActivityLogTimestamp = computed(() => {
    const farmId = this.farmStore.currentFarm()?.id;
    if (!farmId) return null;
    return this.userStore.getUserPreferenceValue(farmId, 'global__lastSeenActivityLogTimestamp') as string | null;
  });

  public newNotificationsCount = computed(() => {
    const lastSeen = this.lastSeenActivityLogTimestamp();
    const logs = this.activityLogs();

    if (!lastSeen) return 0;
    if (logs.length === 0) return 0;

    return logs.filter(log => new Date(log.created_at) > new Date(lastSeen)).length;
  });

  public notifications = computed<notifications[]>(() => {
    const logs = this.activityLogs();
    return logs.map(log => this.mapActivityLogToNotification(log));
  });


  public canShowWeather = computed(() => this.farmStore.canShowWeather());

  public weatherData = computed<WeatherData>(() => {
    const canShow = this.farmStore.canShowWeather();

    if (!canShow) {
      return {
        today: {
          condition: 'layout.header.weather.sunny',
          icon: 'wb_sunny',
          temperature: '--°C',
          temperatureMin: 0,
          temperatureMax: 0
        },
        forecast: []
      };
    }

    const realWeather = this.farmStore.getWidgetWeatherData();

    if (realWeather) {
      return realWeather;
    }

    return {
      today: {
        condition: 'layout.header.weather.sunny',
        icon: 'wb_sunny',
        temperature: '--°C',
        temperatureMin: 0,
        temperatureMax: 0
      },
      forecast: []
    };
  });

  public headerTasks = computed(() => this.taskStore.tasks());

  public taskCount = computed(() => this.headerTasks().length);

  public isMenuOpen(menuName: string): boolean {
    return this.activeMenus[menuName] || false;
  }

  public onMenuOpened(menuName: string) {
    this.activeMenus[menuName] = true;
  }

  public onMenuClosed(menuName: string) {
    this.activeMenus[menuName] = false;
  }

  private mapActivityLogToNotification(log: ActivityLogItem): notifications {
    const iconMap: Record<string, string> = {
      'create': 'solar:add-circle-line-duotone',
      'update': 'solar:pen-line-duotone',
      'delete': 'solar:trash-bin-trash-line-duotone',
      'view': 'solar:eye-line-duotone',
    };

    const colorMap: Record<string, string> = {
      'create': 'success',
      'update': 'primary',
      'delete': 'error',
      'view': 'secondary',
    };

    const timeAgo = this.getTimeAgo(log.created_at);
    const icon = iconMap[log.action] || 'solar:bell-line-duotone';

    return {
      id: log.id,
      icon,
      color: colorMap[log.action] || 'primary',
      time: timeAgo,
      title: log.entity_name || log.entity_type,
      subtitle: log.description,
    };
  }

  private getTimeAgo(date: string | Date): string {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return then.toLocaleDateString();
  }

  public loadMoreActivityLogs(): void {
    if (this.hasMoreActivityLogs() && !this.isLoadingActivityLogs()) {
      this.activityLogStore.loadActivityLogs(
        { skip: this.activityLogs().length, limit: 15 },
        undefined,
        true
      );
    }
  }

  public logout(): void {
    this.authService.signOut();
  }

  public navigateToFarmList(): void {
    this.routerFacade.navigate(['/app/farms']);
  }
}
